// src/api/fnct.api.ts — SCE Issues
// Lecture et import des FNC-T (listes SCE_FNCT et SCE_FNCT_Actions) —
// chantier FNC-T, lot F1.
//
// ─── Où vivent les listes ───
//
// Sur OP_SCE pendant la construction, puis sur QM_SCE après validation de
// tous (décision de Guillaume du 06/10). Le site est désigné par son CHEMIN et
// les listes par leur NOM, retrouvés à la première lecture : ni identifiant ni
// secret GitHub à ajouter, et le déménagement se résume à changer
// `CHEMIN_SITE_FNCT` après avoir recopié les lignes (l'import sait le faire :
// tout est texte, dates et nombres, aucune colonne propre au site).
//
// ─── Noms des colonnes ───
//
// Les colonnes sont retrouvées par leur nom (interne, sinon d'affichage) à la
// première lecture : une liste créée « à partir d'Excel » a des noms internes
// `field_1`… (cf. utils/fnctColonnes, retrouverColonnes). Toute lecture passe
// par `depuisNomsInternes`, toute écriture par `versNomsInternes`.
//
// ─── Lecture ───
//
// ~300 FNC-T et ~800 actions : tout est lu en une fois (pages de 500), gardé
// en mémoire pour la session, filtré et trié côté client — comme le
// référentiel équipements.

import { GraphApiError, graphDelete, graphGet, graphPatch, graphPost } from './graphClient'
import { deposerSauvegardeFnct } from './journalImportFnct.api'
import type { ContenuListesFnct, SauvegardeFnct } from '@/utils/vidageFnct'
import { avecReessai, executerEnPool } from './ecritureEnPool'
import { adresseDe } from './annuaire.api'
import { aujourdhuiParis, jourLisible, memePersonne } from '@/utils/fnct'
import {
  ajouterCommentaireFnct,
  bloquantsCloture,
  changementsFnct,
  changementsRisque,
  conflitsEdition,
  erreurReouverture,
  fnctApres,
  manquantsEdition,
  seulementRisque,
  texteClotureFnct,
  texteModificationFnct,
  type SaisieCloture,
  type SaisieEditionFnct,
  type SaisieRisque,
} from '@/utils/editionFnct'
import {
  COLONNES_ACTIONS,
  COLONNES_FNCT,
  COLONNES_HISTORIQUE,
  NOM_LISTE_ACTIONS,
  NOM_LISTE_FNCT,
  NOM_LISTE_HISTORIQUE,
  actionDepuisChamps,
  colonnesManquantes,
  depuisNomsInternes,
  evenementDepuisChamps,
  fnctDepuisChamps,
  retrouverColonnes,
  typesACorriger,
  versChamps,
  versNomsInternes,
  type ColonneLue,
  type DefColonne,
  type NomsInternes,
  type TypeACorriger,
} from '@/utils/fnctColonnes'
import {
  ajouterAuCommentaire,
  echeancesApresModification,
  erreurCloture,
  erreurConfier,
  erreurModificationEcheance,
  erreurReport,
  erreurSaisieAction,
  ligneConfiee,
  ligneReport,
  prochainIdAction,
  texteAjout,
  texteCloture,
  texteReport,
  type SaisieAction,
} from '@/utils/actionsFnct'
import type { PlanFnct } from '@/utils/fnctImport'
import { texteReattribution } from '@/utils/revueFnct'
import {
  ajouterLigneEcart,
  contientEcart,
  ecartsLisibles,
  ecartVaut,
  estDernierEcart,
  machinesApresAjout,
  remplacerLigneEcart,
  retirerLigneEcart,
  texteEcartModifie,
  texteEcartReleve,
  texteEcartRetire,
  type ChangementEcart,
  type EcartReleve,
} from '@/utils/releveEcarts'
import { texteOuverture } from '@/utils/ouvertureFnct'
import { prochainRang } from '@/utils/escaladeFnct'
import { directsActions, directsOuverture, directsSuite, type Direct, type EvenementGeste, type GesteFnct } from '@/utils/notificationsFnct'
import { acteurNotifications, deposerNotificationsFnct } from './notificationsFnct.api'
import type { ActionFnct, EvenementFnct, Fnct, Jour, TypeEvenementFnct } from '@/types/fnct.types'

/** Hôte et chemin du site qui porte les listes FNC-T. */
const HOTE = 'sartorius.sharepoint.com'
export const CHEMIN_SITE_FNCT = '/sites/OP_SCE'

interface Emplacement {
  site: string
  fncts: string
  actions: string
  /** SCE_FNCT_Historique (lot F3) : facultative — sans elle, rien n'est tracé, rien ne casse. */
  historique: string | null
  /** Colonnes retrouvées, par liste (vide pour un historique absent). */
  noms: { fncts: NomsInternes; actions: NomsInternes; historique: NomsInternes }
}

interface ReponseListes {
  value: { id: string; name?: string; displayName?: string }[]
}

interface Page {
  value: { id: string; fields: Record<string, unknown> }[]
  '@odata.nextLink'?: string
}

let emplacement: Promise<Emplacement> | null = null

/** Identifiants du site, des listes et de leurs colonnes, lus une fois par session. */
function trouverListes(): Promise<Emplacement> {
  emplacement ??= (async () => {
    const site = await graphGet<{ id: string }>(`/sites/${HOTE}:${CHEMIN_SITE_FNCT}?$select=id`)
    const listes = await graphGet<ReponseListes>(`/sites/${site.id}/lists?$select=id,name,displayName&$top=500`)
    const trouver = (nom: string): string => {
      const l = listes.value.find((x) => x.displayName === nom || x.name === nom)
      if (!l) throw new Error(`Liste ${nom} introuvable sur ${CHEMIN_SITE_FNCT} : à créer (docs/chantier-fnct.md § 6)`)
      return l.id
    }
    const historique = listes.value.find((x) => x.displayName === NOM_LISTE_HISTORIQUE || x.name === NOM_LISTE_HISTORIQUE)?.id ?? null
    const fncts = trouver(NOM_LISTE_FNCT)
    const actions = trouver(NOM_LISTE_ACTIONS)
    const colonnesDe = async (liste: string | null): Promise<ColonneLue[]> =>
      liste ? (await graphGet<{ value: ColonneLue[] }>(`/sites/${site.id}/lists/${liste}/columns`)).value : []
    const [cf, ca, ch] = await Promise.all([colonnesDe(fncts), colonnesDe(actions), colonnesDe(historique)])
    return {
      site: site.id,
      fncts,
      actions,
      historique,
      noms: {
        fncts: retrouverColonnes(COLONNES_FNCT, cf),
        actions: retrouverColonnes(COLONNES_ACTIONS, ca),
        historique: retrouverColonnes(COLONNES_HISTORIQUE, ch),
      },
    }
  })().catch((err: unknown) => {
    // Un échec n'est pas gardé : la liste créée entre-temps doit être trouvée au prochain essai.
    emplacement = null
    throw err
  })
  return emplacement
}

type Ligne = Page['value'][number]

const fnctLue = (e: Emplacement) => (l: Ligne): Fnct => fnctDepuisChamps(l.id, depuisNomsInternes(l.fields, e.noms.fncts))
const actionLue = (e: Emplacement) => (l: Ligne): ActionFnct => actionDepuisChamps(l.id, depuisNomsInternes(l.fields, e.noms.actions))

async function lireTout(chemin: string): Promise<Page['value']> {
  const lignes: Page['value'] = []
  let suivante: string | null = `${chemin}/items?$expand=fields&$top=500`
  while (suivante) {
    const page: Page = await graphGet<Page>(suivante)
    lignes.push(...page.value)
    suivante = page['@odata.nextLink'] ?? null
  }
  return lignes
}

export interface DonneesFnct {
  fncts: Fnct[]
  actions: ActionFnct[]
}

let cache: Promise<DonneesFnct> | null = null

/** Toutes les FNC-T et toutes les actions (cache de session). */
export function chargerFnct(): Promise<DonneesFnct> {
  cache ??= (async () => {
    const e = await trouverListes()
    const [fncts, actions] = await Promise.all([
      lireTout(`/sites/${e.site}/lists/${e.fncts}`),
      lireTout(`/sites/${e.site}/lists/${e.actions}`),
    ])
    return {
      fncts: fncts.map(fnctLue(e)).filter((f) => f.numero),
      actions: actions.map(actionLue(e)).filter((a) => a.id),
    }
  })().catch((err: unknown) => {
    cache = null
    throw err
  })
  return cache
}

export function viderCacheFnct(): void {
  cache = null
}

/** Oublie aussi où sont les listes : une liste créée en cours de session (l'historique) est retrouvée. */
export function oublierListesFnct(): void {
  emplacement = null
  cache = null
}

// ─── Vider les listes (bascule, étape 2 — 26/10, utils/vidageFnct) ───

/** Les identifiants des lignes d'une liste, seuls (léger : pour compter et supprimer). */
async function idsDe(chemin: string): Promise<string[]> {
  const ids: string[] = []
  let suivante: string | null = `${chemin}/items?$select=id&$top=999`
  while (suivante) {
    const page: { value: { id: string }[]; '@odata.nextLink'?: string } = await graphGet(suivante)
    ids.push(...page.value.map((l) => l.id))
    suivante = page['@odata.nextLink'] ?? null
  }
  return ids
}

/** Combien de lignes dans chaque liste (l'historique absent compte 0). */
export async function compterListesFnct(): Promise<ContenuListesFnct> {
  const e = await trouverListes()
  const chemin = (liste: string): string => `/sites/${e.site}/lists/${liste}`
  const [fncts, actions, historique] = await Promise.all([
    idsDe(chemin(e.fncts)),
    idsDe(chemin(e.actions)),
    e.historique ? idsDe(chemin(e.historique)) : Promise.resolve([]),
  ])
  return { fncts: fncts.length, actions: actions.length, historique: historique.length }
}

export interface BilanVidage {
  supprimees: ContenuListesFnct
  /** Lignes qui ont résisté (réessayer : le bouton repart de ce qui reste). */
  echecs: number
  /** Chemin de la copie dans SCE_PiecesJointes. */
  sauvegarde: string
}

/**
 * Vide les trois listes FNC-T. D'abord une COPIE de tout, déposée à côté des
 * journaux d'import : si elle échoue, rien n'est supprimé. Puis l'historique, les
 * actions, les FNC-T (les « enfants » d'abord), par petit pool avec réessai ; une
 * ligne déjà partie (404) compte comme supprimée. N'écrit rien dans l'historique
 * (il est vidé), ne notifie personne. L'Excel n'est jamais touché.
 */
export async function viderListesFnct(par: string, onProgression: (p: ProgressionFnct) => void): Promise<BilanVidage> {
  oublierListesFnct()
  const e = await trouverListes()
  const chemin = (liste: string): string => `/sites/${e.site}/lists/${liste}`
  const listes = [
    { nom: NOM_LISTE_HISTORIQUE, cle: 'historique' as const, id: e.historique, noms: e.noms.historique, libelle: 'Suppression de l’historique' },
    { nom: NOM_LISTE_ACTIONS, cle: 'actions' as const, id: e.actions, noms: e.noms.actions, libelle: 'Suppression des actions' },
    { nom: NOM_LISTE_FNCT, cle: 'fncts' as const, id: e.fncts, noms: e.noms.fncts, libelle: 'Suppression des FNC-T' },
  ]

  // 1. La copie, colonnes sous leur nom lisible.
  onProgression({ faits: 0, total: 1, libelle: 'Copie des listes avant vidage' })
  const lues = await Promise.all(listes.map(async (l) => (l.id ? (await lireTout(chemin(l.id))).map((x) => ({ id: x.id, fields: depuisNomsInternes(x.fields, l.noms) })) : [])))
  const sauvegarde: SauvegardeFnct = {
    version: 1,
    le: new Date().toISOString(),
    par,
    listes: Object.fromEntries(listes.map((l, i) => [l.nom, lues[i] ?? []])),
  }
  let cheminSauvegarde: string
  try {
    cheminSauvegarde = await deposerSauvegardeFnct(sauvegarde)
  } catch (err) {
    throw new Error(`La copie des listes n’a pas pu être déposée : rien n’a été supprimé. ${err instanceof Error ? err.message : ''}`.trim())
  }

  // 2. Les suppressions.
  const supprimees: ContenuListesFnct = { fncts: 0, actions: 0, historique: 0 }
  let echecs = 0
  const total = lues.reduce((n, l) => n + l.length, 0)
  let avant = 0
  for (const [i, l] of listes.entries()) {
    const lignes = lues[i] ?? []
    if (!l.id || lignes.length === 0) continue
    const liste = chemin(l.id)
    await executerEnPool(
      lignes.map((x) => x.id),
      async (id) => {
        try {
          await avecReessai(() => graphDelete(`${liste}/items/${id}`))
          supprimees[l.cle] += 1
        } catch (err) {
          if (err instanceof GraphApiError && err.status === 404) supprimees[l.cle] += 1
          else echecs += 1
        }
      },
      (faits) => onProgression({ faits: avant + faits, total, libelle: l.libelle }),
    )
    avant += lignes.length
  }
  oublierListesFnct()
  return { supprimees, echecs, sauvegarde: cheminSauvegarde }
}

// ─── Import ───

export interface ControleListes {
  /** Colonnes attendues absentes, par liste. Vide partout (et types justes) = prêt à écrire. */
  manquantes: { liste: string; colonnes: string[] }[]
  /** Colonnes au mauvais type (« Une ligne de texte » pour une description…), par liste. */
  types: { liste: string; colonnes: TypeACorriger[] }[]
  /** SCE_FNCT_Historique absente : rien ne sera tracé (avertissement, pas blocage). */
  sansHistorique: boolean
}

/**
 * Vérifie que les listes ont toutes leurs colonnes, au bon type. Une colonne
 * absente ou mal typée ferait rejeter TOUTE l'écriture d'une ligne : on le dit
 * avant de commencer. Relit les colonnes : une correction faite entre-temps compte.
 */
export async function controlerListes(): Promise<ControleListes> {
  emplacement = null
  const e = await trouverListes()
  const verifier = <T>(liste: string, colonnes: readonly DefColonne<T>[], noms: NomsInternes) => ({
    manquantes: { liste, colonnes: colonnesManquantes(colonnes, [...noms.keys()]) },
    types: { liste, colonnes: typesACorriger(colonnes, noms) },
  })
  const resultats = [
    verifier(NOM_LISTE_FNCT, COLONNES_FNCT, e.noms.fncts),
    verifier(NOM_LISTE_ACTIONS, COLONNES_ACTIONS, e.noms.actions),
    ...(e.historique ? [verifier(NOM_LISTE_HISTORIQUE, COLONNES_HISTORIQUE, e.noms.historique)] : []),
  ]
  return {
    manquantes: resultats.map((r) => r.manquantes).filter((m) => m.colonnes.length > 0),
    types: resultats.map((r) => r.types).filter((t) => t.colonnes.length > 0),
    sansHistorique: !e.historique,
  }
}

export interface ProgressionFnct {
  faits: number
  total: number
  libelle: string
}

export interface BilanImportFnct {
  crees: number
  modifies: number
  erreurs: { id: string; message: string }[]
}

/**
 * Écrit le plan : FNC-T d'abord, puis leurs actions. Une ligne en échec est
 * notée et n'arrête pas les suivantes.
 */
export async function appliquerPlanFnct(
  plan: PlanFnct,
  onProgress: (p: ProgressionFnct) => void,
): Promise<BilanImportFnct> {
  const e = await trouverListes()
  const bilan: BilanImportFnct = { crees: 0, modifies: 0, erreurs: [] }
  const total =
    plan.fnctsACreer.length + plan.fnctsAModifier.length + plan.actionsACreer.length + plan.actionsAModifier.length
  let deja = 0

  const etape = async <T>(
    taches: T[],
    libelle: string,
    id: (t: T) => string,
    ecrire: (t: T) => Promise<unknown>,
    compter: 'crees' | 'modifies',
  ): Promise<void> => {
    const base = deja
    await executerEnPool(
      taches,
      async (t) => {
        try {
          await avecReessai(() => ecrire(t))
          bilan[compter] += 1
        } catch (err) {
          bilan.erreurs.push({ id: id(t), message: err instanceof Error ? err.message : String(err) })
        }
      },
      (faits) => onProgress({ faits: base + faits, total, libelle }),
    )
    deja += taches.length
  }

  const listeFnct = `/sites/${e.site}/lists/${e.fncts}`
  const listeActions = `/sites/${e.site}/lists/${e.actions}`

  const nf = e.noms.fncts
  const na = e.noms.actions

  await etape(plan.fnctsACreer, 'Création des FNC-T', (f) => f.numero,
    (f) => graphPost(`${listeFnct}/items`, { fields: versNomsInternes(versChamps(f, f.numero, COLONNES_FNCT), nf) }), 'crees')
  await etape(plan.fnctsAModifier, 'Mise à jour des FNC-T', (m) => m.element.numero,
    (m) => graphPatch(`${listeFnct}/items/${m.itemId}/fields`, versNomsInternes(champsModifies(versChamps(m.element, m.element.numero, COLONNES_FNCT, true), m.ecarts), nf)), 'modifies')
  await etape(plan.actionsACreer, 'Création des actions', (a) => a.id,
    (a) => graphPost(`${listeActions}/items`, { fields: versNomsInternes(versChamps(a, a.id, COLONNES_ACTIONS), na) }), 'crees')
  await etape(plan.actionsAModifier, 'Mise à jour des actions', (m) => m.element.id,
    (m) => graphPatch(`${listeActions}/items/${m.itemId}/fields`, versNomsInternes(champsModifies(versChamps(m.element, m.element.id, COLONNES_ACTIONS, true), m.ecarts), na)), 'modifies')

  viderCacheFnct()
  return bilan
}

/** PATCH partiel : seulement les colonnes qui changent. */
function champsModifies(
  champs: Record<string, string | number | null>,
  ecarts: readonly { colonne: string }[],
): Record<string, string | number | null> {
  const res: Record<string, string | number | null> = {}
  for (const e of ecarts) res[e.colonne] = champs[e.colonne] ?? null
  return res
}

// ─── Adresses (07/10) ───

/**
 * Les adresses manquantes (LeaderUPN, ResponsableUPN), retrouvées dans
 * l'annuaire d'après le « Prénom Nom » : noms pré-remplis (modèle P&ID / PPL,
 * déclarant), choisis avant que l'annuaire réponde. Au mieux : vide si la
 * personne n'est pas retrouvée à coup sûr — l'écriture n'attend jamais l'annuaire.
 */
async function completerAdresses(fnct: Fnct, actions: ActionFnct[]): Promise<{ fnct: Fnct; actions: ActionFnct[] }> {
  const [leaderUPN, ...adresses] = await Promise.all([
    // Un relevé n'a pas encore de leader : rien à chercher.
    fnct.leaderUPN || (fnct.leader.trim() ? adresseDe(fnct.leader) : ''),
    ...actions.map((a) => a.responsableUPN || adresseDe(a.responsable)),
  ])
  return {
    fnct: { ...fnct, leaderUPN: leaderUPN ?? '' },
    actions: actions.map((a, i) => ({ ...a, responsableUPN: adresses[i] ?? '' })),
  }
}

// ─── Création (lot F2) ───

/**
 * Les actions d'une ouverture : écrites, et celles restées en route (réseau
 * coupé, SharePoint saturé…), à réessayer depuis l'écran « C'est ouvert ».
 */
export interface ActionsOuverture {
  ecrites: ActionFnct[]
  nonEcrites: ActionFnct[]
}

/**
 * Écrit les actions d'une FNC-T qui vient d'être écrite, SANS s'arrêter à la
 * première qui échoue (02/11) : la FNC-T existe déjà, une erreur ici faisait
 * croire qu'il fallait tout recommencer — un second clic créait une seconde FNC-T.
 * Chacune est tentée (pool de 3, réessai sur 429 / 503 / 504) ; celles qui
 * échouent sont rendues dans l'ordre du plan, pour « Réessayer » (reessayerActions).
 */
async function ecrireActionsOuverture(e: Emplacement, actions: readonly ActionFnct[]): Promise<ActionsOuverture> {
  const liste = `/sites/${e.site}/lists/${e.actions}`
  const ecrites: (ActionFnct | null)[] = actions.map(() => null)
  await executerEnPool(
    actions.map((_, i) => i),
    async (i) => {
      const a = actions[i]
      if (!a) return
      try {
        const cree = await avecReessai(() =>
          graphPost<{ id: string }>(`${liste}/items`, { fields: versNomsInternes(versChamps(a, a.id, COLONNES_ACTIONS), e.noms.actions) }),
        )
        ecrites[i] = { ...a, itemId: cree?.id }
      } catch (err) {
        console.warn(`[fnct] action ${a.id} non écrite :`, err)
      }
    },
    () => undefined,
  )
  return {
    ecrites: ecrites.filter((a): a is ActionFnct => a !== null),
    nonEcrites: actions.filter((_, i) => ecrites[i] === null),
  }
}

/**
 * Crée une FNC-T et ses actions. Le N° est pris APRÈS relecture de la liste
 * (le plus grand de l'année + 1), puis vérifié : si quelqu'un a créé le même
 * N° au même instant, la ligne écrite en second prend le suivant. Jamais
 * recalculé ensuite.
 *
 * Une erreur AVANT l'écriture de la FNC-T remonte : rien n'est écrit, on peut
 * recommencer. Après (02/11), plus jamais : la FNC-T existe, les actions qui
 * n'ont pas pu être écrites sont rendues (`nonEcrites`) pour « Réessayer ».
 * @param construire fabrique la FNC-T et ses actions pour un rang donné.
 * @returns le N° attribué, les actions écrites et celles restées en route.
 */
export async function creerFnct(
  annee: number,
  construire: (rang: number) => { fnct: Fnct; actions: ActionFnct[] },
  /** Texte de l'événement « Création », d'après les actions ÉCRITES ; par défaut, la description et ces actions. */
  texteCreation?: (ecrites: readonly ActionFnct[]) => string,
): Promise<{ numero: string } & ActionsOuverture> {
  const e = await trouverListes()
  const listeFnct = `/sites/${e.site}/lists/${e.fncts}`
  const lues = (await lireTout(listeFnct)).map(fnctLue(e))
  const construireComplet = (r: number): Promise<{ fnct: Fnct; actions: ActionFnct[] }> => {
    const c = construire(r)
    return completerAdresses(c.fnct, c.actions)
  }
  let { fnct, actions } = await construireComplet(prochainRang(lues, annee))
  const cree = await graphPost<{ id: string }>(`${listeFnct}/items`, {
    fields: versNomsInternes(versChamps(fnct, fnct.numero, COLONNES_FNCT), e.noms.fncts),
  })

  // Deux créations simultanées : la plus ancienne ligne garde le N°. Le N° ne
  // change ici qu'une fois la ligne réécrite : interrompu, il reste celui de la ligne.
  try {
    for (let essai = 0; essai < 5; essai += 1) {
      const toutes = (await lireTout(listeFnct)).map(fnctLue(e))
      const memeNumero = toutes.filter((f) => f.numero.toUpperCase() === fnct.numero.toUpperCase())
      const avant = memeNumero.some((f) => Number(f.itemId) < Number(cree.id))
      if (!avant) break
      const rang = prochainRang(toutes, annee)
      const suivant = await construireComplet(rang)
      await graphPatch(`${listeFnct}/items/${cree.id}/fields`, versNomsInternes({ Title: suivant.fnct.numero, Rang: rang }, e.noms.fncts))
      ;({ fnct, actions } = suivant)
    }
  } catch (err) {
    console.warn('[fnct] contrôle du N° interrompu :', err)
  }

  const { ecrites, nonEcrites } = await ecrireActionsOuverture(e, actions)
  const contenu = texteCreation ? texteCreation(ecrites) : [fnct.description, ...ecrites.map((a) => texteAjout(a))].join('\n')
  await tracerFnct(fnct.numero, 'Création', fnct.enregistrePar, contenu)
  viderCacheFnct()
  // Un relevé (sans Ouverture) n'est pas encore une FNC-T ouverte : personne à prévenir.
  const numero = fnct.numero
  if (fnct.ouverture) notifierFnct((d) => gesteFnct(d, numero, [{ type: 'Création', contenu }], directsOuverture))
  return { numero, ecrites, nonEcrites }
}

// ─── Relevé d'écarts P&ID / PPL (14/10, docs/chantier-fnct.md § 13) ───

/**
 * Crée la FNC-T en relevé au premier écart : N° pris tout de suite (mêmes
 * règles que creerFnct : le plus grand de l'année + 1, relu), Ouverture vide,
 * aucune action. Trace « Création ».
 * @returns le N° attribué.
 */
export async function creerReleve(annee: number, construire: (rang: number) => Fnct, ecart: EcartReleve): Promise<string> {
  const machines = construire(0).numerosSerie.join(', ')
  const { numero } = await creerFnct(annee, (rang) => ({ fnct: construire(rang), actions: [] }), () => `Relevé d’écarts P&ID / PPL (${machines})\n${texteEcartReleve(ecart, [])}`)
  return numero
}

/**
 * Ajoute un écart au relevé d'une FNC-T, sans jamais écraser celui d'un autre :
 * relire la ligne, ajouter la ligne d'écart (et les machines cochées qui n'y sont
 * pas), écrire, relire ; si l'écart n'est pas dans la ligne relue (un autre a
 * écrit en même temps), recommencer. Refusé si la FNC-T a été ouverte ou close
 * entre-temps. Trace « Écart relevé ».
 */
export async function ajouterEcartAuReleve(numero: string, ecart: EcartReleve, machines: readonly string[], auteur: string): Promise<void> {
  const e = await trouverListes()
  const cle = numero.toUpperCase()
  const ligne = (await lireTout(`/sites/${e.site}/lists/${e.fncts}`)).map(fnctLue(e)).find((f) => f.numero.toUpperCase() === cle)
  if (!ligne?.itemId) throw new Error(`${numero} introuvable dans SCE_FNCT.`)
  let ajoutees: string[] = []
  for (let essai = 0; essai < 5; essai += 1) {
    const { chemin, fnct: relue, noms } = await relireFnct(ligne.itemId)
    if (relue.cloture) throw new Error(`${numero} a été close entre-temps : l’écart n’a pas été ajouté.`)
    if (relue.ouverture) throw new Error(`${numero} a été ouverte entre-temps : l’écart n’a pas été ajouté.`)
    // Déjà là (un essai précédent a abouti sans qu'on le voie) : rien à réécrire.
    if (!contientEcart(relue.ecarts, ecart.id)) {
      const numerosSerie = machinesApresAjout(relue.numerosSerie, machines)
      if (essai === 0) ajoutees = numerosSerie.slice(relue.numerosSerie.length)
      await ecrireEcarts(chemin, relue, { ...relue, ecarts: ajouterLigneEcart(relue.ecarts, ecart), numerosSerie }, noms)
    }
    const apres = await relireFnct(ligne.itemId)
    if (contientEcart(apres.fnct.ecarts, ecart.id)) {
      const contenu = texteEcartReleve(ecart, ajoutees)
      await tracerFnct(relue.numero, 'Écart relevé', auteur, contenu)
      viderCacheFnct()
      notifierFnct((d) => gesteFnct(d, relue.numero, [{ type: 'Écart relevé', contenu }]))
      return
    }
  }
  viderCacheFnct()
  throw new Error(`${numero} est modifiée en même temps par quelqu’un d’autre : l’écart n’a pas pu être ajouté, réessayez.`)
}

/**
 * Corrige (`m`) ou retire (`m` = null) un écart d'un relevé, depuis sa fiche
 * (22/10). Même prudence que l'ajout : la ligne est RELUE, refusée si la FNC-T a
 * été ouverte ou close entre-temps, si l'écart n'y est plus, ou s'il est le
 * dernier (un retrait) ; seule la colonne Ecarts est réécrite, l'écart corrigé
 * garde sa place ; relue après écriture pour vérifier, 5 essais ; trace
 * « Écart modifié » ou « Écart retiré ». Ses photos restent dans leur dossier.
 */
export async function corrigerEcartDuReleve(numero: string, id: string, m: ChangementEcart | null, auteur: string): Promise<void> {
  const e = await trouverListes()
  const cle = numero.toUpperCase()
  const ligne = (await lireTout(`/sites/${e.site}/lists/${e.fncts}`)).map(fnctLue(e)).find((f) => f.numero.toUpperCase() === cle)
  if (!ligne?.itemId) throw new Error(`${numero} introuvable dans SCE_FNCT.`)
  const fait = (col: string | undefined): boolean => (m ? ecartVaut(col, id, m) : !contientEcart(col, id))
  let original: EcartReleve | undefined
  for (let essai = 0; essai < 5; essai += 1) {
    const { chemin, fnct: relue, noms } = await relireFnct(ligne.itemId)
    if (relue.cloture) throw new Error(`${numero} a été close entre-temps : rien n’a été changé.`)
    if (relue.ouverture) throw new Error(`${numero} a été ouverte entre-temps : ses écarts se corrigent maintenant dans la FNC-T.`)
    const present = ecartsLisibles(relue.ecarts).find((x) => x.id === id)
    if (essai === 0) {
      if (!present) throw new Error('Cet écart n’est plus dans le relevé (retiré par quelqu’un d’autre) : rafraîchissez la fiche.')
      if (!m && estDernierEcart(relue.ecarts)) throw new Error('C’est le dernier écart du relevé : corrigez-le plutôt que de le retirer.')
      original = present
    }
    if (!fait(relue.ecarts)) {
      const ecarts = m ? remplacerLigneEcart(relue.ecarts, id, m) : retirerLigneEcart(relue.ecarts, id)
      await ecrireEcarts(chemin, relue, { ...relue, ecarts }, noms)
    }
    const apres = await relireFnct(ligne.itemId)
    if (fait(apres.fnct.ecarts)) {
      const evenement: EvenementGeste | null = original
        ? { type: m ? 'Écart modifié' : 'Écart retiré', contenu: m ? texteEcartModifie(original, m) : texteEcartRetire(original) }
        : null
      if (evenement) await tracerFnct(relue.numero, evenement.type, auteur, evenement.contenu)
      viderCacheFnct()
      if (evenement) notifierFnct((d) => gesteFnct(d, relue.numero, [evenement]))
      return
    }
  }
  viderCacheFnct()
  throw new Error(`${numero} est modifiée en même temps par quelqu’un d’autre : l’écart n’a pas pu être ${m ? 'corrigé' : 'retiré'}, réessayez.`)
}

/**
 * Ouvre la FNC-T d'un relevé (étape 4) : la ligne est RELUE, refusée si elle a
 * été ouverte ou close entre-temps, puis reconstruite sur ce qu'elle est
 * maintenant (`construire` : un écart relevé par un autre pendant la saisie
 * entre aussi). Seules les colonnes qui changent sont écrites ; les actions sont
 * créées à la suite de celles qui existeraient ; trace « Ouverture ». Comme
 * creerFnct : une fois la FNC-T marquée ouverte, plus d'erreur — les actions qui
 * n'ont pas pu être écrites sont rendues pour « Réessayer » (02/11).
 * @returns la FNC-T ouverte, ses actions écrites et celles restées en route.
 */
export async function ouvrirReleve(
  numero: string,
  construire: (relue: Fnct, actionsDeja: number) => { fnct: Fnct; actions: ActionFnct[]; nbEcarts: number },
  auteur: string,
): Promise<{ fnct: Fnct } & ActionsOuverture> {
  const e = await trouverListes()
  const cle = numero.toUpperCase()
  const ligne = (await lireTout(`/sites/${e.site}/lists/${e.fncts}`)).map(fnctLue(e)).find((f) => f.numero.toUpperCase() === cle)
  if (!ligne?.itemId) throw new Error(`${numero} introuvable dans SCE_FNCT.`)
  const { chemin, fnct: relue, noms } = await relireFnct(ligne.itemId)
  if (relue.cloture) throw new Error(`${numero} a été close entre-temps.`)
  if (relue.ouverture) throw new Error(`${numero} a déjà été ouverte, le ${jourLisible(relue.ouverture)} : rafraîchissez la fiche.`)
  const listeActions = `/sites/${e.site}/lists/${e.actions}`
  const deja = (await lireTout(listeActions)).map(actionLue(e)).filter((a) => a.fnct.toUpperCase() === cle).length
  const construit = construire(relue, deja)
  const { fnct, actions } = await completerAdresses(construit.fnct, construit.actions)
  await ecrireEcarts(chemin, relue, fnct, noms)
  const { ecrites, nonEcrites } = await ecrireActionsOuverture(e, actions)
  const contenu = texteOuverture(fnct, ecrites, construit.nbEcarts)
  await tracerFnct(numero, 'Ouverture', auteur, contenu)
  viderCacheFnct()
  notifierFnct((d) => gesteFnct(d, numero, [{ type: 'Ouverture', contenu }], directsOuverture))
  return { fnct, ecrites, nonEcrites }
}

/** Même action : même texte, même responsable (une action écrite malgré l'erreur, la réponse perdue en chemin). */
function memeAction(x: Pick<ActionFnct, 'description' | 'responsable'>, a: Pick<ActionFnct, 'description' | 'responsable'>): boolean {
  return x.description.trim() === a.description.trim() && memePersonne(x.responsable, a.responsable)
}

/**
 * « Réessayer » de l'écran « C'est ouvert » (02/11) : écrit, sur la même FNC-T, les
 * actions restées en route à l'ouverture. Une action déjà là (écrite malgré l'erreur,
 * la réponse perdue en chemin) n'est pas réécrite ; son N° pris entre-temps par un
 * ajout depuis la fiche, elle prend le suivant. Chacune écrite est tracée « Action
 * ajoutée » et son responsable prévenu, comme un ajout depuis la fiche. Jamais
 * d'erreur : ce qui n'a toujours pas pu l'être est rendu, avec la dernière raison.
 */
export async function reessayerActions(
  numero: string,
  actions: readonly ActionFnct[],
  auteur: string,
): Promise<ActionsOuverture & { erreur: string | null }> {
  const message = (err: unknown): string => (err instanceof Error ? err.message : String(err))
  let e: Emplacement
  let existantes: ActionFnct[]
  try {
    e = await trouverListes()
    const cle = numero.toUpperCase()
    existantes = (await lireTout(`/sites/${e.site}/lists/${e.actions}`)).map(actionLue(e)).filter((a) => a.fnct.toUpperCase() === cle)
  } catch (err) {
    return { ecrites: [], nonEcrites: [...actions], erreur: message(err) }
  }
  const ecrites: ActionFnct[] = []
  const nonEcrites: ActionFnct[] = []
  let erreur: string | null = null
  for (const a of actions) {
    try {
      const memeId = existantes.find((x) => x.id.toUpperCase() === a.id.toUpperCase())
      const deja = memeId && memeAction(memeId, a) ? memeId : null
      const ecrite = deja ?? (await ecrireActionNumerotee(e, memeId ? { ...a, id: prochainIdAction(existantes, numero) } : a))
      if (!deja) existantes.push(ecrite)
      ecrites.push(ecrite)
      await tracerFnct(numero, 'Action ajoutée', auteur, texteAjout(ecrite))
    } catch (err) {
      erreur = message(err)
      nonEcrites.push(a)
    }
  }
  viderCacheFnct()
  if (ecrites.length > 0) {
    notifierFnct((d) => gesteFnct(d, numero, ecrites.map((x) => ({ type: 'Action ajoutée' as const, contenu: texteAjout(x) })), () => directsActions(ecrites)))
  }
  return { ecrites, nonEcrites, erreur }
}

// ─── Historique (lot F3) ───

/**
 * Trace un événement dans SCE_FNCT_Historique. Best-effort : l'action métier
 * est déjà écrite ; une liste absente ou un refus ne la défait pas.
 * @returns vrai si l'événement est écrit.
 */
export async function tracerFnct(numero: string, type: TypeEvenementFnct, auteur: string, contenu: string): Promise<boolean> {
  try {
    const e = await trouverListes()
    if (!e.historique) return false
    const evt: EvenementFnct = { fnct: numero, date: new Date().toISOString(), auteur, type, contenu }
    await graphPost(`/sites/${e.site}/lists/${e.historique}/items`, {
      fields: versNomsInternes(versChamps(evt, numero, COLONNES_HISTORIQUE), e.noms.historique),
    })
    return true
  } catch (err) {
    console.warn(`[fnct] historique non tracé (${numero}, ${type}) :`, err)
    return false
  }
}

// ─── Notifications (lot F6, 23/10) ───

/**
 * Prévient qui doit l'être après un geste (utils/notificationsFnct) : les
 * listes sont relues (`chargerFnct`, après `viderCacheFnct`), les messages
 * déposés dans SCE_Envois pour le flow 6. Sans attendre ni échouer : le geste est
 * fait, l'écran n'attend pas les mails.
 */
export function notifierFnct(construire: (d: DonneesFnct) => GesteFnct[]): void {
  if (!acteurNotifications()) return
  void chargerFnct()
    .then((d) => deposerNotificationsFnct(construire(d)))
    .catch((err: unknown) => console.warn('[fnct] notifications non envoyées :', err))
}

/** Le geste sur `numero`, d'après les listes relues ; [] si la FNC-T est introuvable. */
export function gesteFnct(
  d: DonneesFnct,
  numero: string,
  evenements: EvenementGeste[],
  directs: (f: Fnct, actions: ActionFnct[]) => Direct[] = () => [],
): GesteFnct[] {
  const cle = numero.toUpperCase()
  const fnct = d.fncts.find((f) => f.numero.toUpperCase() === cle)
  if (!fnct) return []
  return [{ fnct, evenements, directs: directs(fnct, d.actions.filter((a) => a.fnct.toUpperCase() === cle)) }]
}

/** L'historique d'une FNC-T, du plus récent au plus ancien ; vide sans la liste. */
export async function lireHistorique(numero: string): Promise<EvenementFnct[]> {
  const e = await trouverListes()
  if (!e.historique) return []
  const cle = numero.toUpperCase()
  return (await lireTout(`/sites/${e.site}/lists/${e.historique}`))
    .map((l) => evenementDepuisChamps(l.id, depuisNomsInternes(l.fields, e.noms.historique)))
    .filter((v) => v.fnct.toUpperCase() === cle)
    .sort((a, b) => b.date.localeCompare(a.date))
}

// ─── Modifier, clore, rouvrir la FNC-T (07/10) ───

/** Relit une FNC-T avant de l'écrire : on agit sur ce qu'elle est MAINTENANT. */
async function relireFnct(itemId: string): Promise<{ chemin: string; fnct: Fnct; noms: NomsInternes }> {
  const e = await trouverListes()
  const chemin = `/sites/${e.site}/lists/${e.fncts}/items/${itemId}`
  const l = await graphGet<Ligne>(`${chemin}?$expand=fields`)
  return { chemin, fnct: fnctLue(e)(l), noms: e.noms.fncts }
}

/** Écrit les seules colonnes qui diffèrent entre `avant` et `apres` (null efface). */
async function ecrireEcarts(chemin: string, avant: Fnct, apres: Fnct, noms: NomsInternes): Promise<void> {
  const a = versChamps(avant, avant.numero, COLONNES_FNCT, true)
  const b = versChamps(apres, avant.numero, COLONNES_FNCT, true)
  const changes = Object.keys(b).filter((k) => k !== 'Title' && b[k] !== a[k])
  if (changes.length === 0) return
  await graphPatch(`${chemin}/fields`, versNomsInternes(Object.fromEntries(changes.map((k) => [k, b[k] ?? null])), noms))
}

/**
 * Modifie une FNC-T (utils/editionFnct). Relue : refusée si un champ modifié
 * ici a bougé depuis l'ouverture du formulaire.
 * @param ouverte la FNC-T telle qu'affichée à l'ouverture du formulaire.
 * @param avecRisque faux : S, F, D et l'analyse AQ ne sont jamais écrits.
 * @returns faux si rien n'a changé.
 */
export async function modifierFnct(ouverte: Fnct, saisie: SaisieEditionFnct, avecRisque: boolean, auteur: string): Promise<boolean> {
  if (!ouverte.itemId) throw new Error('FNC-T sans identifiant SharePoint.')
  const manquants = manquantsEdition(saisie)
  if (manquants.length > 0) throw new Error(`Il manque ${manquants.join(', ')}.`)
  const { chemin, fnct: relue, noms } = await relireFnct(ouverte.itemId)
  let apres = fnctApres(ouverte, saisie, avecRisque)
  const changements = changementsFnct(ouverte, apres)
  if (changements.length === 0) return false
  const conflits = conflitsEdition(ouverte, relue, changements)
  if (conflits.length > 0) {
    throw new Error(`Modifié par quelqu'un d'autre entre-temps (${conflits.join(', ')}) : rafraîchissez la fiche et recommencez.`)
  }
  // Leader changé sans adresse (nom pré-rempli) : l'annuaire, au mieux.
  if (apres.leader !== relue.leader && !apres.leaderUPN) apres = { ...apres, leaderUPN: (await adresseDe(apres.leader)) ?? '' }
  // On écrit SUR la ligne relue : un champ que l'on ne touche pas reste tel qu'un autre l'a mis.
  const cible: Fnct = { ...relue }
  for (const c of changements) Object.assign(cible, { [c.cle]: apres[c.cle] })
  if (changements.some((c) => c.cle === 'leader')) cible.leaderUPN = apres.leaderUPN
  await ecrireEcarts(chemin, relue, cible, noms)
  const evenement: EvenementGeste = { type: seulementRisque(changements) ? 'Risque évalué' : 'Modification', contenu: texteModificationFnct(changements) }
  await tracerFnct(ouverte.numero, evenement.type, auteur, evenement.contenu)
  viderCacheFnct()
  // Un nouveau leader ou un nouvel expert apprend son rôle.
  const roles = changements.flatMap((c) => (c.cle === 'leader' || c.cle === 'expert' ? [c.cle] : []))
  notifierFnct((d) =>
    gesteFnct(d, ouverte.numero, [evenement], (f) =>
      roles.flatMap((role): Direct[] => {
        const nom = (role === 'leader' ? f.leader : f.expert).trim()
        const upn = role === 'leader' ? (f.leaderUPN ?? '') : ''
        return nom ? [{ genre: 'role', role, quand: 'changement', personne: { nom, upn } }] : []
      }),
    ),
  )
  return true
}

/**
 * Évalue le risque d'une FNC-T (S, F, D, analyse AQ) — section « Le risque » ou
 * fenêtre de clôture (04/11). Relue avant écriture ; seuls ces champs sont écrits.
 * @returns false si rien n'a changé.
 */
export async function evaluerRisqueFnct(ouverte: Fnct, risque: SaisieRisque, auteur: string): Promise<boolean> {
  if (!ouverte.itemId) throw new Error('FNC-T sans identifiant SharePoint.')
  const changements = changementsRisque(ouverte, risque)
  if (changements.length === 0) return false
  const { chemin, fnct: relue, noms } = await relireFnct(ouverte.itemId)
  const conflits = conflitsEdition(ouverte, relue, changements)
  if (conflits.length > 0) {
    throw new Error(`Modifié par quelqu'un d'autre entre-temps (${conflits.join(', ')}) : rafraîchissez la fiche et recommencez.`)
  }
  const cible: Fnct = { ...relue }
  const apres: Fnct = { ...ouverte, ...risque, analyseRisque: risque.analyseRisque.trim() }
  for (const c of changements) Object.assign(cible, { [c.cle]: apres[c.cle] })
  await ecrireEcarts(chemin, relue, cible, noms)
  const evenement: EvenementGeste = { type: 'Risque évalué', contenu: texteModificationFnct(changements) }
  await tracerFnct(ouverte.numero, evenement.type, auteur, evenement.contenu)
  viderCacheFnct()
  notifierFnct((d) => gesteFnct(d, ouverte.numero, [evenement]))
  return true
}

/**
 * Clôt une FNC-T. Relue, ses actions aussi : refusée si l'une a été rouverte
 * (ou ajoutée) entre-temps, ou si la FNC-T est déjà close.
 */
export async function cloreFnct(ouverte: Fnct, saisie: SaisieCloture, aujourdhui: Jour, auteur: string): Promise<void> {
  if (!ouverte.itemId) throw new Error('FNC-T sans identifiant SharePoint.')
  const e = await trouverListes()
  const { chemin, fnct: relue, noms } = await relireFnct(ouverte.itemId)
  const cle = relue.numero.toUpperCase()
  const actions = (await lireTout(`/sites/${e.site}/lists/${e.actions}`)).map(actionLue(e)).filter((a) => a.fnct.toUpperCase() === cle)
  const bloquants = bloquantsCloture(relue, actions, saisie, aujourdhui)
  if (bloquants.length > 0) throw new Error(`Impossible de clore : ${bloquants.join(', ')}.`)
  const apres: Fnct = {
    ...relue,
    cloture: saisie.cloture,
    clotureCorrection: saisie.clotureCorrection,
    legitimite: saisie.legitimite,
    numeroCapa: saisie.numeroCapa.trim(),
    commentaires: ajouterCommentaireFnct(relue.commentaires, saisie.commentaire),
  }
  await ecrireEcarts(chemin, relue, apres, noms)
  const contenu = texteClotureFnct(relue, saisie)
  await tracerFnct(relue.numero, 'Clôture', auteur, contenu)
  viderCacheFnct()
  notifierFnct((d) => gesteFnct(d, relue.numero, [{ type: 'Clôture', contenu }]))
}

/** Rouvre une FNC-T close : date de clôture effacée, motif dans l'historique ; le reste est gardé. */
export async function rouvrirFnct(ouverte: Fnct, motif: string, auteur: string): Promise<void> {
  if (!ouverte.itemId) throw new Error('FNC-T sans identifiant SharePoint.')
  const { chemin, fnct: relue, noms } = await relireFnct(ouverte.itemId)
  const erreur = erreurReouverture(relue, motif)
  if (erreur) throw new Error(erreur)
  await ecrireEcarts(chemin, relue, { ...relue, cloture: null }, noms)
  const contenu = `Était close le ${jourLisible(relue.cloture)}\n${motif.trim()}`
  await tracerFnct(relue.numero, 'Réouverture', auteur, contenu)
  viderCacheFnct()
  notifierFnct((d) => gesteFnct(d, relue.numero, [{ type: 'Réouverture', contenu }]))
}

// ─── Les actions (lot F3) ───

/** Relit une action avant de l'écrire : on agit sur ce qu'elle est MAINTENANT. */
async function relireAction(itemId: string): Promise<{ chemin: string; action: ActionFnct; noms: NomsInternes }> {
  const e = await trouverListes()
  const chemin = `/sites/${e.site}/lists/${e.actions}/items/${itemId}`
  const l = await graphGet<Ligne>(`${chemin}?$expand=fields`)
  return { chemin, action: actionLue(e)(l), noms: e.noms.actions }
}

/**
 * Réattribue des actions ouvertes à une autre personne (lot 6 du § 14, 22/10 :
 * départ, absence). Chaque action est RELUE : passée, sans rien écrire, si elle a
 * été close ou confiée à quelqu'un d'autre entre-temps. Responsable, adresse
 * (et service s'il est connu) écrits ; une trace « Action réattribuée » chacune.
 * @returns combien ont été réattribuées, et celles qui ont été passées.
 */
export async function reattribuerActions(
  itemIds: readonly string[],
  de: string,
  vers: { nom: string; upn: string; service: string },
  auteur: string,
  /** Pourquoi elles changent de mains (obligatoire depuis le 30/10) : dans le commentaire de chacune et l'historique. */
  motif: string,
  aujourdhui: Jour = aujourdhuiParis(),
): Promise<{ faites: number; passees: string[] }> {
  if (!vers.nom.trim()) throw new Error('Choisissez à qui confier les actions.')
  if (!motif.trim()) throw new Error('Dites pourquoi ces actions changent de mains.')
  let faites = 0
  const passees: string[] = []
  const reattribuees: { action: ActionFnct; contenu: string }[] = []
  await executerEnPool(
    [...itemIds],
    async (itemId) => {
      const { chemin, action, noms } = await relireAction(itemId)
      if (action.cloture || !memePersonne(action.responsable, de)) {
        passees.push(action.id)
        return
      }
      const apres: ActionFnct = {
        ...action,
        responsable: vers.nom.trim(),
        responsableUPN: vers.upn || (await adresseDe(vers.nom)),
        service: vers.service.trim() || action.service,
        commentaire: ajouterAuCommentaire(action.commentaire, ligneConfiee(action.responsable, vers.nom, motif, aujourdhui)),
      }
      const avant = versChamps(action, action.id, COLONNES_ACTIONS, true)
      const voulu = versChamps(apres, action.id, COLONNES_ACTIONS, true)
      const changes = Object.keys(voulu).filter((k) => k !== 'Title' && voulu[k] !== avant[k])
      if (changes.length > 0) {
        await avecReessai(() => graphPatch(`${chemin}/fields`, versNomsInternes(Object.fromEntries(changes.map((k) => [k, voulu[k] ?? null])), noms)))
        const contenu = texteReattribution(action, action.responsable, apres.responsable, motif)
        await tracerFnct(action.fnct, 'Action réattribuée', auteur, contenu)
        reattribuees.push({ action: apres, contenu })
      }
      faites += 1
    },
    () => undefined,
  )
  viderCacheFnct()
  // Un message par FNC-T : la personne reçoit ses actions, les abonnés le changement.
  const numeros = [...new Set(reattribuees.map((x) => x.action.fnct))]
  notifierFnct((d) =>
    numeros.flatMap((numero) => {
      const ici = reattribuees.filter((x) => x.action.fnct === numero)
      return gesteFnct(
        d,
        numero,
        ici.map((x): EvenementGeste => ({ type: 'Action réattribuée', contenu: x.contenu })),
        () => directsActions(ici.map((x) => x.action), de, motif),
      )
    }),
  )
  return { faites, passees }
}

/**
 * Confie UNE action à quelqu'un d'autre (30/10, Guillaume : « commentaire
 * obligatoire ») : son responsable, ou qui gère les actions. Relue : refusée si
 * elle a été close ou confiée à quelqu'un d'autre entre-temps (`de` : le
 * responsable affiché au clic). Responsable, adresse et service écrits, le motif
 * en ligne du commentaire (« reassigned to … (was …): … ») ; trace « Action
 * réattribuée » ; le nouveau responsable est prévenu, motif compris.
 */
export async function confierAction(
  itemId: string,
  de: string,
  vers: { nom: string; upn: string; service: string },
  motif: string,
  aujourdhui: Jour,
  auteur: string,
): Promise<void> {
  const { chemin, action, noms } = await relireAction(itemId)
  if ((action.responsable.trim() || de.trim()) && !memePersonne(action.responsable, de)) {
    throw new Error(`Cette action a changé de responsable entre-temps (${action.responsable.trim() || 'personne'}) : rafraîchissez la page.`)
  }
  const erreur = erreurConfier(action, vers, motif)
  if (erreur) throw new Error(erreur)
  const apres: ActionFnct = {
    ...action,
    responsable: vers.nom.trim(),
    responsableUPN: vers.upn || (await adresseDe(vers.nom)),
    service: vers.service.trim() || action.service,
    commentaire: ajouterAuCommentaire(action.commentaire, ligneConfiee(action.responsable, vers.nom, motif, aujourdhui)),
  }
  const avant = versChamps(action, action.id, COLONNES_ACTIONS, true)
  const voulu = versChamps(apres, action.id, COLONNES_ACTIONS, true)
  const changes = Object.keys(voulu).filter((k) => k !== 'Title' && voulu[k] !== avant[k])
  await graphPatch(`${chemin}/fields`, versNomsInternes(Object.fromEntries(changes.map((k) => [k, voulu[k] ?? null])), noms))
  const contenu = texteReattribution(action, action.responsable.trim() || 'personne', apres.responsable, motif)
  await tracerFnct(action.fnct, 'Action réattribuée', auteur, contenu)
  viderCacheFnct()
  notifierFnct((d) => gesteFnct(d, action.fnct, [{ type: 'Action réattribuée', contenu }], () => directsActions([apres], action.responsable.trim() || undefined, motif)))
}

/**
 * Écrit UNE action et lui garantit un N° à elle (02/11) : écrite, puis relue ; si
 * une autre action de la FNC-T porte le même N° et a été écrite AVANT (deux ajouts
 * à la même seconde), celle-ci prend le suivant — la règle du N° d'une FNC-T
 * (creerFnct). L'action est écrite dès le POST : un contrôle interrompu la laisse
 * avec le N° qu'elle porte, sans erreur (sinon on l'ajouterait deux fois).
 * @returns l'action telle qu'écrite : N° final, itemId.
 */
async function ecrireActionNumerotee(e: Emplacement, action: ActionFnct): Promise<ActionFnct> {
  const liste = `/sites/${e.site}/lists/${e.actions}`
  const cree = await graphPost<{ id: string }>(`${liste}/items`, {
    fields: versNomsInternes(versChamps(action, action.id, COLONNES_ACTIONS), e.noms.actions),
  })
  let id = action.id
  try {
    for (let essai = 0; essai < 5; essai += 1) {
      const toutes = (await lireTout(liste)).map(actionLue(e))
      const avant = toutes.some((a) => a.id.toUpperCase() === id.toUpperCase() && Number(a.itemId) < Number(cree.id))
      if (!avant) break
      const suivant = prochainIdAction(toutes, action.fnct)
      await graphPatch(`${liste}/items/${cree.id}/fields`, versNomsInternes({ Title: suivant }, e.noms.actions))
      id = suivant
    }
  } catch (err) {
    console.warn('[fnct] contrôle du N° de l’action interrompu :', err)
  }
  return { ...action, id, itemId: cree.id }
}

/** Ajoute une action à une FNC-T ; son ID suit la plus grande de la FNC-T, unique même pour deux ajouts simultanés. */
export async function ajouterAction(numero: string, saisie: SaisieAction, aujourdhui: Jour, auteur: string): Promise<ActionFnct> {
  const erreur = erreurSaisieAction(saisie)
  if (erreur) throw new Error(erreur)
  const e = await trouverListes()
  const liste = `/sites/${e.site}/lists/${e.actions}`
  const existantes = (await lireTout(liste)).map(actionLue(e))
  const action: ActionFnct = {
    id: prochainIdAction(existantes, numero),
    fnct: numero,
    categorie: saisie.categorie,
    description: saisie.description.trim(),
    responsable: saisie.responsable.trim(),
    responsableUPN: saisie.responsableUPN || (await adresseDe(saisie.responsable)),
    service: saisie.service.trim(),
    site: 'Pompey',
    ouverture: aujourdhui,
    echeance: saisie.echeance,
    echeanceReportee: null,
    cloture: null,
    commentaire: '',
  }
  const ecrite = await ecrireActionNumerotee(e, action)
  const contenu = texteAjout(ecrite)
  await tracerFnct(numero, 'Action ajoutée', auteur, contenu)
  viderCacheFnct()
  notifierFnct((d) => gesteFnct(d, numero, [{ type: 'Action ajoutée', contenu }], () => directsActions([ecrite])))
  return ecrite
}

/** Clôt une action (relue : refusée si quelqu'un l'a close entre-temps). */
export async function cloreAction(itemId: string, cloture: Jour, commentaire: string, aujourdhui: Jour, auteur: string): Promise<void> {
  const { chemin, action, noms } = await relireAction(itemId)
  const erreur = erreurCloture(action, cloture, aujourdhui)
  if (erreur) throw new Error(erreur)
  const champs: Record<string, string> = { DateCloture: versChamps({ ...action, cloture }, action.id, COLONNES_ACTIONS).DateCloture as string }
  if (commentaire.trim()) champs.Commentaire = ajouterAuCommentaire(action.commentaire, commentaire)
  await graphPatch(`${chemin}/fields`, versNomsInternes(champs, noms))
  const contenu = texteCloture(action, cloture, commentaire)
  await tracerFnct(action.fnct, 'Action close', auteur, contenu)
  viderCacheFnct()
  // P&ID / PPL : le BE a mis à jour, la suite peut démarrer.
  notifierFnct((d) =>
    gesteFnct(d, action.fnct, [{ type: 'Action close', contenu }], (f, actions) =>
      directsSuite(f, actions, actions.find((a) => a.id === action.id) ?? { ...action, cloture }),
    ),
  )
}

/** Reporte l'échéance, autant de fois que nécessaire (30/10) : la dernière date est gardée, chaque motif dans le commentaire. */
export async function reporterAction(itemId: string, nouvelle: Jour, motif: string, aujourdhui: Jour, auteur: string): Promise<void> {
  const { chemin, action, noms } = await relireAction(itemId)
  const erreur = erreurReport(action, nouvelle, motif)
  if (erreur) throw new Error(erreur)
  const champs = {
    EcheanceReportee: versChamps({ ...action, echeanceReportee: nouvelle }, action.id, COLONNES_ACTIONS).EcheanceReportee,
    Commentaire: ajouterAuCommentaire(action.commentaire, ligneReport(nouvelle, motif, aujourdhui)),
  }
  await graphPatch(`${chemin}/fields`, versNomsInternes(champs, noms))
  const contenu = texteReport(action, nouvelle, motif)
  await tracerFnct(action.fnct, 'Action reportée', auteur, contenu)
  viderCacheFnct()
  notifierFnct((d) => gesteFnct(d, action.fnct, [{ type: 'Action reportée', contenu }]))
}

/**
 * Modifie une action ouverte : catégorie, texte, responsable, échéance. Un
 * responsable qui change demande son motif (30/10, comme « Confier ») : il rejoint
 * le commentaire de l'action et l'historique.
 */
export async function modifierAction(itemId: string, saisie: SaisieAction, auteur: string, motif = '', aujourdhui: Jour = aujourdhuiParis()): Promise<void> {
  const erreur = erreurSaisieAction(saisie)
  if (erreur) throw new Error(erreur)
  const { chemin, action, noms } = await relireAction(itemId)
  if (action.cloture) throw new Error('Une action close ne se modifie plus.')
  const erreurEcheance = erreurModificationEcheance(action, saisie.echeance)
  if (erreurEcheance) throw new Error(erreurEcheance)
  const autrePersonne = !memePersonne(saisie.responsable, action.responsable)
  if (autrePersonne && action.responsable.trim() && !motif.trim()) throw new Error('Dites pourquoi l’action change de responsable.')
  const apres: ActionFnct = {
    ...action,
    categorie: saisie.categorie,
    description: saisie.description.trim(),
    responsable: saisie.responsable.trim(),
    // Même personne sans nouvelle adresse : on garde la sienne ; sinon l'annuaire.
    responsableUPN:
      saisie.responsableUPN ||
      (memePersonne(saisie.responsable, action.responsable) ? action.responsableUPN : '') ||
      (await adresseDe(saisie.responsable)),
    service: saisie.service.trim(),
    // Reportée : la date saisie est l'échéance en vigueur (la reportée), l'initiale ne bouge pas.
    ...echeancesApresModification(action, saisie.echeance),
    // Confiée à quelqu'un d'autre : le motif rejoint le commentaire (30/10).
    commentaire:
      autrePersonne && motif.trim() ? ajouterAuCommentaire(action.commentaire, ligneConfiee(action.responsable, saisie.responsable, motif, aujourdhui)) : action.commentaire,
  }
  const avant = versChamps(action, action.id, COLONNES_ACTIONS, true)
  const voulu = versChamps(apres, action.id, COLONNES_ACTIONS, true)
  const changes = Object.keys(voulu).filter((k) => k !== 'Title' && voulu[k] !== avant[k])
  if (changes.length === 0) return
  await graphPatch(`${chemin}/fields`, versNomsInternes(Object.fromEntries(changes.map((k) => [k, voulu[k] ?? null])), noms))
  const contenu = [`${action.description}\n${texteAjout({ ...apres, echeance: apres.echeanceReportee ?? apres.echeance })}`, autrePersonne ? motif.trim() : ''].filter(Boolean).join('\n')
  await tracerFnct(action.fnct, 'Action modifiée', auteur, contenu)
  viderCacheFnct()
  // Confiée à quelqu'un d'autre : le nouveau responsable l'apprend, motif compris.
  notifierFnct((d) =>
    gesteFnct(d, action.fnct, [{ type: 'Action modifiée', contenu }], () => (autrePersonne ? directsActions([apres], action.responsable.trim() || undefined, motif) : [])),
  )
}
